import React from 'react';

import { useForm } from 'react-hook-form';
import { observer } from 'mobx-react';

import { TextArea } from '@infoods/lifebase-ui';

import classes from './UpsertNoteModal.module.scss';
import { LbBigModal } from '../../../../shared/components/modals';
import { NoteFormValues, UsersListOption } from '../../../interfaces/note';
import {
  LbMultipleAutocomplete,
  OptionWithDescription,
} from '../../../../shared/components/form-components';
import { notesStore } from '../../../stores/notesStore';
import { apiStatusStore } from '../../../../shared/stores/apiStatusStore';
import { getNoteRequest } from '../utils/getNoteRequest';

type UpsertNoteModalProps = {
  noteId?: number;
  defaultValues?: NoteFormValues;
  usersList?: UsersListOption[];
  onClose: () => void;
};

const UpsertNoteModal = observer(
  ({ noteId, defaultValues, usersList, onClose }: UpsertNoteModalProps) => {
    const { isLoading } = apiStatusStore;

    const { control, handleSubmit } = useForm<NoteFormValues>({
      defaultValues,
    });

    const { createNote, updateNote } = notesStore;

    const onSubmit = handleSubmit(async (data) => {
      const noteRequest = getNoteRequest(data);

      if (noteId) {
        await updateNote(noteId, noteRequest);
      } else {
        await createNote(noteRequest);
      }
      onClose();
    });

    const generalInputProps = {
      control,
      size: 'small',
      variant: 'outlined',
    } as const;

    return (
      <LbBigModal
        open
        title={noteId ? 'Edit note' : 'Add a new note'}
        description="Enter the name(s) of the user(s) to assign this note to"
        isLoading={isLoading}
        buttonOptions={{
          yesText: 'Confirm',
          noText: 'Cancel',
          yesHandler: onSubmit,
          noHandler: onClose,
        }}
      >
        <div className={classes.container}>
          <LbMultipleAutocomplete
            {...generalInputProps}
            name="users"
            valueKey="id"
            labelKey="name"
            rules={{ required: 'Select at least one user' }}
            options={usersList || []}
            label="Assigned Users"
            renderOptionLabel={(option) => (
              <OptionWithDescription label={option.name} description={option.email} />
            )}
          />
          <TextArea
            {...generalInputProps}
            name="note"
            label="Comment"
            rules={{ required: 'Comment is required' }}
          />
        </div>
      </LbBigModal>
    );
  },
);

export { UpsertNoteModal };
